<template>
  <div style="width: 650px">
    <div class="d-input-group">
      <label class="d-label marg-05-bottom">Vælg medarbejder</label>
      <select class="do-select" v-model="theSelectedUserId" @change="onUserIsSelected">
        <option disabled>Vælg person</option>
        <option v-for="user in users" :key="user.id" :value="user.id">
          {{ user.name }}
        </option>
      </select>
    </div>
    <div class="d-input-group">
      <label class="d-label">Indtast første og sidste arbejdsdag for personen (begge dage inklusiv)</label>
      <div class="d-group-flex">
        <DatePickerRange
          :startdate="startDate"
          :enddate="endDate"
          @startchanged="setStartDate"
          @endchanged="setEndDate($event, true)">
        </DatePickerRange>
        <p class="d-link" @click="resetDates" v-if="!isDatesPristine">{{ $t('vue2.task_overview.reset') }}</p>
      </div>
    </div>
    <div class="d-input-group">
      <label class="d-label">Antal feriedage afholdt i perioden</label>
      <input type="number" class="d-input__number" v-model="numberOfVacationDays">
    </div>
    <div class="d-input-group">
      <label class="d-label">Timer pr. uge</label>
      <input type="number" class="d-input__number" v-model="hoursPerWeek">
    </div>
    <h4>Udregning</h4>
    <table class="do-table">
      <thead>
      <th>For perioden</th>
      <th>Dage</th>
      <th>Timer</th>
      </thead>
      <tbody>
      <tr>
        <td>Antal dage</td>
        <td>{{ numberOfDays }}</td>
        <td></td>
      </tr>
      <tr>
        <td>Antal dage uden weekender</td>
        <td>{{ numberOfDaysWithoutWeekends }}</td>
        <td>{{ numberOfDaysWithoutWeekendsInHours }}</td>
      </tr>
      <tr :content="holidaysAsLabels"
          v-tippy="{
                    arrow: true,
                    placement : 'bottom',
                    onShow: () => hasAnyHolidays
                  }">
        <td>Antal søgnehellidage på hverdage</td>
        <td>{{ filteredHolidays.length }}</td>
        <td>{{ filteredHolidaysInHours }}</td>
      </tr>
      <tr>
        <td>Antal arbejdsdage fratrukket søgnehellidage</td>
        <td>{{ numberOfDaysWithoutWeekendsAndHolidays }}</td>
        <td>{{ numberOfDaysWithoutWeekendsAndHolidaysInHours }}</td>
      </tr>
      <tr>
        <td>Ferie afholdt</td>
        <td>{{ numberOfVacationDays }}</td>
        <td>{{ numberOfVacationDaysInHours }}</td>
      </tr>
      <tr class="d-diff">
        <td>Mulig arbejdstid</td>
        <td>{{ numberOfWorkDaysPossible }}</td>
        <td>{{ numberOfWorkDaysPossibleInHours }}</td>
      </tr>
      <template v-if="userData">
        <tr class="d-diff">
          <td><b>Præsteret arbejdstid</b></td>
          <td></td>
          <td>{{ userActualHours }}</td>
        </tr>
        <tr class="d-diff" v-if="userData">
          <td>Difference i arbejdstid</td>
          <td></td>
          <td v-if="userActualHoursDiff">{{ userActualHoursDiff > 0 ? '+' : '' }}{{ userActualHoursDiff }}</td>
        </tr>
      </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import moment from 'moment'
import DatePickerRange from '../datepicker/DatePickerRange.vue'
import ChangeSchoolYear from '../School/ChangeSchoolYear.vue'
import docendo from '../../docendo'

export default {
  components: {ChangeSchoolYear, DatePickerRange},
  props: {
    schoolId: {},
    users: {
      type: Array,
      default: []
    }
  },
  data () {
    return {
      hoursPerWeek: 37,
      userData: null,
      isLoadingUpdatedNumbers: false,
      theSelectedUserId: null,
      numberOfVacationDays: 0,
      startDate: moment().startOf('year').format('YYYY-MM-DD'), // customize your start date
      endDate: moment().endOf('year').format('YYYY-MM-DD') // customize your end date
    }
  },
  computed: {
    ...mapGetters({
      holidaysWithinActiveSchoolYear: 'holidaysWithinActiveSchoolYear'
    }),
    hoursPerDay () {
      return this.hoursPerWeek / 5
    },
    userActualHours () {
      return this.userData && this.userData[0].actual_total_hours ? (this.userData[0].actual_total_hours * 1).toFixed(1) : 0
    },
    userActualHoursDiff () {
      return ((this.userActualHours * 1) - (this.numberOfWorkDaysPossibleInHours * 1)).toFixed(2)
    },
    theSelectedUser () {
      return this.theSelectedUserId ? this.users.find(user => user.id === this.theSelectedUserId) : null
    },
    numberOfWorkDaysPossible () {
      return this.numberOfDaysWithoutWeekendsAndHolidays - this.numberOfVacationDays
    },
    numberOfWorkDaysPossibleInHours () {
      return (this.numberOfWorkDaysPossible * this.hoursPerDay).toFixed(1)
    },
    numberOfVacationDaysInHours () {
      return (this.numberOfVacationDays * this.hoursPerDay).toFixed(1)
    },
    filteredHolidays () {
      return this.holidaysWithinActiveSchoolYear(this.startDate, this.endDate)
    },
    hasAnyHolidays () {
      return this.filteredHolidays.length > 0
    },
    holidaysAsLabels () {
      return this.filteredHolidays.join(', ')
    },
    filteredHolidaysInHours () {
      return (this.filteredHolidays.length * this.hoursPerDay).toFixed(1)
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    isDatesPristine () {
      return this.compareDates(this.activeSchoolYear.employee_time_start, this.startDate) && this.compareDates(this.activeSchoolYear.employee_time_end, this.endDate)
    },
    numberOfDays () {
      const start = moment(this.startDate).startOf('day')
      const end = moment(this.endDate).endOf('day')
      const diff = end.diff(start, 'day')
      return diff + 1 // to include the endDate
    },
    numberOfDaysWithoutWeekendsInHours () {
      return (this.numberOfDaysWithoutWeekends * this.hoursPerDay).toFixed(1)
    },
    numberOfDaysWithoutWeekends () {
      const diff = this.numberOfDays
      let newDate = moment(this.startDate)
      let weekdaysCount = 0
      for (let i = 0; i < diff; i++) {
        if (newDate.isoWeekday() !== 6 && newDate.isoWeekday() !== 7) {
          weekdaysCount++
        }
        newDate = moment(newDate).add(1, 'days')
      }
      return weekdaysCount
    },
    numberOfDaysWithoutWeekendsAndHolidays () {
      return this.numberOfDaysWithoutWeekends - this.filteredHolidays.length
    },
    numberOfDaysWithoutWeekendsAndHolidaysInHours () {
      return (this.numberOfDaysWithoutWeekendsAndHolidays * this.hoursPerDay).toFixed(1)
    },
    summary () {
      return {
        userId: this.theSelectedUserId,
        startDate: this.startDate,
        endDate: this.endDate,
        hoursPerWeek: this.hoursPerWeek * 1,
        grossHours: this.numberOfDaysWithoutWeekendsInHours * 1,
        holidayHours: this.filteredHolidaysInHours * 1,
        vacationHours: this.numberOfVacationDaysInHours * 1,
        workHours: this.numberOfWorkDaysPossibleInHours * 1
      }
    }
  },
  watch: {
    summary: {
      handler (summary) {
        this.$emit('changed', summary)
      },
      immediate: true
    },
    activeSchoolYear () {
      this.setEndDate(this.activeSchoolYear.employee_time_end)
      this.setStartDate(this.activeSchoolYear.employee_time_start)
    }
  },
  methods: {
    onUserIsSelected () {
      this.getUsersHours()
    },
    getUsersHours () {
      if (!this.theSelectedUserId) {
        return
      }
      this.isLoadingUpdatedNumbers = true
      const startDate = moment(this.startDate).format('YYYY-MM-DD') + ' 00:00'
      const endDate = moment(this.endDate).format('YYYY-MM-DD') + ' 23:59'
      docendo.schoolActualHours(this.schoolId, startDate, endDate, [this.theSelectedUserId], data => {
        this.userData = data
        this.isLoadingUpdatedNumbers = false
      })
    },
    resetDates () {
      this.setEndDate(this.activeSchoolYear.employee_time_end, false)
      this.setStartDate(this.activeSchoolYear.employee_time_start)
    },
    compareDates (firstDate, secondDate) {
      return moment(firstDate).format('YYYY-MM-DD') === moment(secondDate).format('YYYY-MM-DD')
    },
    setStartDate (date) {
      this.startDate = moment(date).format('YYYY-MM-DD')
      if (moment(date).isValid()) {
        this.getUsersHours()
      }
    },
    setEndDate (date, isCallServer) {
      this.endDate = moment(date).format('YYYY-MM-DD')
      if (!isCallServer) {
        return
      }
      if (moment(date).isValid()) {
        this.getUsersHours()
      }
    }
  },
  mounted () {
    if (this.activeSchoolYear) {
      this.setEndDate(this.activeSchoolYear.employee_time_end, false)
      this.setStartDate(this.activeSchoolYear.employee_time_start)
    }
  }
}
</script>

<style scoped>
.do-table {
  border-collapse: collapse;
  background-color: #ffffff;
  width: 100%;
}

.do-table thead th {
  padding: 0.5rem;
}

.do-table thead th:first-child,
.do-table tbody td:first-child {
  max-width: 300px;
}

.do-table thead th:nth-child(2),
.do-table tbody td:nth-child(2),
.do-table thead th:nth-child(3),
.do-table tbody td:nth-child(3) {
  text-align: right;
}

.do-table > thead, .d-table > tfoot {
  border: 1px solid #e2e8f0;
  text-transform: uppercase;
  font-weight: bold;
  text-align: left;
  background: #f6f9fc;
}

.d-input__number {
  width: 210px;
  text-align: center;
  background-color: #fff;
  box-shadow: 0 2px 5px -1px rgba(50, 50, 93, .25), 0 1px 3px -1px rgba(0, 0, 0, .3);
  border: 1px solid #edf2f7;
}

.do-table > tfoot tr:last-child {
  font-size: 13px;
}

.do-table > thead > tr > th,
.do-table > tfoot > tr > td {
  padding: 5px;
}

.do-table > thead > tr:first-child,
.do-table > tfoot > tr:first-child {
  color: #8898aa;
}

.do-table tr th,
.do-table > tfoot tr td {
  padding: 4px 8px;
}

.do-table tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

.do-table tr th h4 {
  color: #3e4450;
}

.do-table tr td {
  padding: 0 8px;
}

.d-input-group {
  margin: 0 0 1rem 0;
}

.do-select {
  background-color: #fff;
  box-shadow: 0 2px 5px -1px rgba(50, 50, 93, .25), 0 1px 3px -1px rgba(0, 0, 0, .3);
  border: 1px solid #edf2f7;
  width: 210px;
  font-size: 13px;
  border-radius: 4px;
  height: 34px;
}

.marg-05-bottom {
  margin-bottom: 0.5rem;
}

.d-diff td {
  padding: 0.5rem !important;
  font-weight: bold;
}

.d-group-flex {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.d-link {
  color: var(--blue);
  cursor: pointer;
}

</style>



// WEBPACK FOOTER //
// AssignHoursEmploymentEnded.vue?0801163f